import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CATEGORIES = ["Wet Rooms & Walk-In Showers", "Bathroom Renovation Projects", "Floor Tiling", "Wall Tiling", "45° Mitred Tile Edges", "Large Format Tiles"];
export default function RecentLondonTransformations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const fetchMedia = async () => {
      const data = [{
        id: 1,
        location: "Kensington",
        type: "Luxury Wet Room",
        category: "Wet Rooms & Walk-In Showers",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/luxury_wet_room_kensington.png",
        quality: 95,
        height: "h-72"
      }, {
        id: 2,
        location: "Chelsea",
        type: "Modern Suite",
        category: "Bathroom Renovation Projects",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/modern_bathroom_renovation_chelsea.png",
        quality: 98,
        height: "h-64"
      }, {
        id: 3,
        location: "Wandsworth",
        type: "Porcelain Floor",
        category: "Floor Tiling",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/porcelain_floor_tiling_wandsworth.png",
        quality: 90,
        height: "h-56"
      }, {
        id: 4,
        location: "Fulham",
        type: "Marble Walls",
        category: "Wall Tiling",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/marble_wall_tiling_fulham.png",
        quality: 92,
        height: "h-72"
      }, {
        id: 5,
        location: "Hampstead",
        type: "Mitred Corner",
        category: "45° Mitred Tile Edges",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/mitred_tile_edge_precision_hampstead.png",
        quality: 99,
        height: "h-48"
      }, {
        id: 6,
        location: "Richmond",
        type: "Slab Install",
        category: "Large Format Tiles",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/large_format_slab_installation_richmond.png",
        quality: 96,
        height: "h-60"
      }, {
        id: 7,
        location: "Islington",
        type: "Walk-in Shower",
        category: "Wet Rooms & Walk-In Showers",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/walk_in_shower_islington.png",
        quality: 88,
        height: "h-64"
      }, {
        id: 8,
        location: "Battersea",
        type: "Full Refurb",
        category: "Bathroom Renovation Projects",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/complete_bathroom_refurbishment_battersea.png",
        quality: 94,
        height: "h-72"
      }, {
        id: 9,
        location: "Notting Hill",
        type: "Kitchen Floor",
        category: "Floor Tiling",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/kitchen_floor_tiling_notting_hill.png",
        quality: 85,
        height: "h-56"
      }, {
        id: 10,
        location: "Mayfair",
        type: "Feature Wall",
        category: "Wall Tiling",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/luxury_feature_wall_mayfair.png",
        quality: 91,
        height: "h-64"
      }, {
        id: 11,
        location: "Belgravia",
        type: "Edge Detail",
        category: "45° Mitred Tile Edges",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/precision_mitred_corner_belgravia.png",
        quality: 97,
        height: "h-52"
      }, {
        id: 12,
        location: "Knightsbridge",
        type: "Grand Format",
        category: "Large Format Tiles",
        image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/grand_format_porcelain_knightsbridge.png",
        quality: 93,
        height: "h-72"
      }];
      setTimeout(() => {
        setProjects(data);
        setIsLoading(false);
      }, 600);
    };
    fetchMedia();
  }, []);
  return <section id="recent-london-transformations" ref={ref} className="py-12 transition-opacity duration-700 opacity-100 !bg-[hsl(210,16%,93%)]">
      <div className="container ml-0 mr-auto px-4 mb-12 text-left">
        <h2 className="text-3xl font-bold text-black mb-4 tracking-tight !bg-[hsl(210,16%,93%)] md:text-4xl">
          Recent London Transformations
        </h2>
        <p className="text-black max-w-2xl ml-0 mr-auto text-lg !bg-[hsl(210,14%,89%)]">
          Explore our portfolio of precision tiling and luxury bathroom renovations across the capital, organized by our specialist services.
        </p>
      </div>

      <div className="container ml-0 mr-auto px-4 !bg-[hsl(210,16%,93%)]">
        {isLoading ? <div className="flex flex-col items-start justify-start py-20 gap-4">
            <Loader className="w-8 h-8 animate-spin text-[var(--ring)]" />
            <p className="text-muted-foreground animate-pulse">Loading gallery items...</p>
          </div> : <div className="space-y-16">
            {CATEGORIES.map((category, catIndex) => {
          const categoryProjects = projects.filter(p => p.category === category).sort((a, b) => b.quality - a.quality);
          if (categoryProjects.length === 0) return null;
          return <div key={category} data-index={catIndex} className="group/category">
                  <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-xl md:text-2xl font-bold text-black whitespace-nowrap">
                      {category}
                    </h3>
                    <div className="h-px bg-border w-full" />
                  </div>
                  
                  <div className="columns-1 md:columns-2 lg:columns-3 gap-0.5 space-y-0.5">
                    {categoryProjects.map((project, projIndex) => <div key={project.id} data-index={projIndex} className="relative group break-inside-avoid overflow-hidden bg-muted">
                        <img src={project.image} alt={`${project.type} in ${project.location}`} className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${project.height}`} loading="lazy" onError={e => {
                  (e.target as HTMLImageElement).src = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/tiling_project_fallback.png";
                }} />
                        
                        <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                          <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                            <div className="flex items-center gap-2 text-[var(--ring)] font-medium mb-1">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm uppercase tracking-wider text-black">
                                {project.location}
                              </span>
                            </div>
                            <h3 className="text-black text-xl font-semibold">
                              {project.type}
                            </h3>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>}

        <div className="mt-16 flex flex-col items-start gap-6">
          <p className="text-black italic">
            Viewing our latest premium highlights from across London
          </p>
          <Link to="#">
            <Button variant="outline" className="gap-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors rounded-none h-12 px-8">
              View Full Portfolio <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}